import * as Api from '@ViewModels';
import { AIConversationType } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useAccountProfileCreateMutation } from '../../../queries/AccountProfile/useAccountProfileCreateMutation';
import {
	invalidateAccountProfileQueries,
	useAccountProfileQuery,
} from '../../../queries/AccountProfile/useAccountProfileQuery';
import { useAccountProfileUpdateMutation } from '../../../queries/AccountProfile/useAccountProfileUpdateMutation';
import { invalidateAllAIConversationFilterQueries } from '../../../queries/AIConversation/useAIConversationFilterQuery';
import { BusinessInfo, parseBusinessDescription } from '../../../utils/businessInfoParser';
import { AIAssistantDialog } from '../../../web/components-v2/ai/AIAssistantDialog';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { TextArea } from '../../../web/components-v2/TextArea';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../web/components/Modal';
import { styles } from './styles';

interface IProps extends IModalContext<void> {
	account?: Api.IAccount;
	className?: string;
	styles?: StyleDeclarationValue[];
}

/**
 * Simple info field component with label over value
 */
const InfoField: React.FC<{ label: string; value: string | undefined }> = ({ label, value }) => {
	return (
		<div className={css(styles.infoFieldContainer)}>
			<div className={css(styles.infoFieldLabel)}>{label}</div>
			<div className={css(styles.infoFieldValue)}>{value || '-'}</div>
		</div>
	);
};

/**
 * Component to display structured business information
 */
const BusinessInfoDisplay: React.FC<{ businessInfo: BusinessInfo | null; keywords?: string }> = ({
	businessInfo,
	keywords,
}) => {
	return (
		<div className={css(styles.businessInfoGrid)}>
			{/* Row 1: Location and Website */}
			<InfoField label='Location' value={businessInfo?.location || '-'} />
			<InfoField label='Website' value={businessInfo?.website || '-'} />

			{/* Row 2: Phone number and Pronouns */}
			<InfoField label='Phone number' value={businessInfo?.phoneNumber || '-'} />
			<InfoField label='Pronouns' value={businessInfo?.pronouns || '-'} />

			{/* Row 3: Tone preference */}
			<InfoField label='Tone preference' value={businessInfo?.tonePreference || '-'} />

			{/* Row 4: Description (full width) */}
			<div className={css(styles.fullWidthSection)}>
				<div className={css(styles.infoFieldLabel)}>Description</div>
				<div className={css(styles.descriptionValue)}>{businessInfo?.description || '-'}</div>
			</div>

			{/* Row 5: Keywords (full width) */}
			<div className={css(styles.fullWidthSection)}>
				<div className={css(styles.infoFieldLabel)}>Keywords</div>
				<div className={css(styles.infoFieldValue)}>{keywords || '-'}</div>
			</div>
		</div>
	);
};

// Character limits for validation
const MAX_BUSINESS_DESCRIPTION_CHARS = 3000;
const MAX_KEYWORDS_CHARS = 9000;

const CustomContentPreferencesModalSFC: React.FC<IProps> = props => {
	const { account, className } = props;
	const [aiAssistantOpen, setAiAssistantOpen] = React.useState(false);
	const [isEditMode, setIsEditMode] = React.useState(false);
	const [editableBusinessDescription, setEditableBusinessDescription] = React.useState('');
	const [editableKeywords, setEditableKeywords] = React.useState('');
	const [descriptionValidationError, setDescriptionValidationError] = React.useState<string>('');
	const [keywordsValidationError, setKeywordsValidationError] = React.useState<string>('');
	const [websiteUrl, setWebsiteUrl] = React.useState('');

	// Pre-populate website URL from account if available
	React.useEffect(() => {
		if (account?.features?.websites?.websiteUrl && !websiteUrl) {
			setWebsiteUrl(account.features.websites.websiteUrl);
		}
	}, [account?.features?.websites?.websiteUrl, websiteUrl]);

	const impersonationContext = React.useMemo(() => ({ account }), [account]);
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
	const errorMessages = useErrorMessages();

	const accountProfileCreateMutation = useAccountProfileCreateMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});
	const accountProfileUpdateMutation = useAccountProfileUpdateMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const isSaving = accountProfileCreateMutation.isPending || accountProfileUpdateMutation.isPending;

	const accountName = account?.companyName || 'your account';
	const initialMessage = React.useMemo(() => {
		if (websiteUrl.trim()) {
			return `I'll help you configure Custom Content Preferences for "${accountName}." Analyzing the website now...`;
		}
		return `I'll help you configure Custom Content Preferences for "${accountName}." Checking their current settings...`;
	}, [accountName, websiteUrl]);

	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			setAiAssistantOpen(open);
			if (!open) {
				// When dialog closes, invalidate the query to refetch the latest data
				invalidateAccountProfileQueries({ impersonationContext });
				// Clear website URL when dialog closes
				setWebsiteUrl('');
			} else {
				// When dialog opens, invalidate conversation filter queries to clear stale conversation cache
				invalidateAllAIConversationFilterQueries();
			}
		},
		[impersonationContext]
	);

	const customInitialAction = React.useMemo(() => {
		if (!websiteUrl.trim()) {
			return undefined;
		}
		return {
			_type: 'ContentSettingsIntakeAction',
			websiteUrl: websiteUrl.trim(),
		} as Api.ContentSettingsIntakeAction;
	}, [websiteUrl]);

	const businessDescription = accountProfileQuery.data?.businessDescription;
	const keywords = accountProfileQuery.data?.messagingPreferences?.keywords;

	// Try to parse the business description into structured format
	const parsedBusinessInfo = businessDescription ? parseBusinessDescription(businessDescription) : null;

	// Contextual button text based on whether data exists
	const hasAnyData = parsedBusinessInfo || keywords;
	const buttonText = hasAnyData ? 'Edit with AI Assistant' : 'Configure with AI Assistant';

	const handleEditDirectlyClick = () => {
		setEditableBusinessDescription(businessDescription || '');
		setEditableKeywords(keywords || '');
		setDescriptionValidationError('');
		setKeywordsValidationError('');
		setIsEditMode(true);
	};

	const handleCancelEdit = () => {
		setIsEditMode(false);
		setEditableBusinessDescription('');
		setEditableKeywords('');
		setDescriptionValidationError('');
		setKeywordsValidationError('');
	};

	const handleBusinessDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setEditableBusinessDescription(value);
		if (value.length > MAX_BUSINESS_DESCRIPTION_CHARS) {
			setDescriptionValidationError(
				`Business description must be ${MAX_BUSINESS_DESCRIPTION_CHARS} characters or less`
			);
		} else {
			setDescriptionValidationError('');
		}
	};

	const handleKeywordsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setEditableKeywords(value);
		if (value.length > MAX_KEYWORDS_CHARS) {
			setKeywordsValidationError(`Keywords must be ${MAX_KEYWORDS_CHARS} characters or less`);
		} else {
			setKeywordsValidationError('');
		}
	};

	const handleSave = async () => {
		const accountProfile: Api.AccountProfile = {
			businessDescription: editableBusinessDescription,
			id: accountProfileQuery.data?.id ?? null,
		};
		if (editableKeywords) {
			accountProfile.messagingPreferences = { keywords: editableKeywords };
		}

		try {
			await (accountProfile.id
				? accountProfileUpdateMutation.mutateAsync({ accountProfile, impersonationContext })
				: accountProfileCreateMutation.mutateAsync({ accountProfile, impersonationContext }));
			invalidateAccountProfileQueries({ impersonationContext });
			setIsEditMode(false);
			toast.success('Custom content preferences saved successfully');
		} catch {
			// Error already handled by mutation onError
		}
	};

	return (
		<div className={`${css(styles.container)} custom-content-preferences-modal ${className || ''}`}>
			<h1 className={css(styles.header)}>Custom Content Preferences</h1>
			<section className={css(styles.content)}>
				{accountProfileQuery.isPending ? (
					<div className={css(styles.loadingContainer)}>
						<LoadingSpinner />
					</div>
				) : accountProfileQuery.isError ? (
					<p className={css(styles.notConfiguredText)}>Error loading preferences. Please try again.</p>
				) : isEditMode ? (
					<>
						<div className={css(styles.fieldContainer)}>
							<div className={css(styles.infoFieldLabel)}>Business Description</div>
							<TextArea
								value={editableBusinessDescription}
								onChange={handleBusinessDescriptionChange}
								rows={15}
								maxLength={MAX_BUSINESS_DESCRIPTION_CHARS}
								placeholder='Enter business description with structured fields like Location:, Website:, etc.'
							/>
							{descriptionValidationError && (
								<div className={css(styles.errorMessage)}>{descriptionValidationError}</div>
							)}
							<div
								className={css(
									styles.characterCount,
									editableBusinessDescription.length > MAX_BUSINESS_DESCRIPTION_CHARS && styles.characterCountError
								)}
							>
								{editableBusinessDescription.length} / {MAX_BUSINESS_DESCRIPTION_CHARS} characters
							</div>
						</div>
						<div className={css(styles.fieldContainer)}>
							<div className={css(styles.infoFieldLabel)}>Keywords</div>
							<TextArea
								value={editableKeywords}
								onChange={handleKeywordsChange}
								rows={15}
								maxLength={MAX_KEYWORDS_CHARS}
								placeholder='Enter keywords separated by commas'
							/>
							{keywordsValidationError && <div className={css(styles.errorMessage)}>{keywordsValidationError}</div>}
							<div
								className={css(
									styles.characterCount,
									editableKeywords.length > MAX_KEYWORDS_CHARS && styles.characterCountError
								)}
							>
								{editableKeywords.length} / {MAX_KEYWORDS_CHARS} characters
							</div>
						</div>
						<div className={css(styles.buttonContainer)}>
							<Button
								variant='primary'
								size='sm'
								onClick={handleSave}
								disabled={isSaving || !!descriptionValidationError || !!keywordsValidationError}
							>
								Save
							</Button>
							<Button variant='secondary' size='sm' onClick={handleCancelEdit} disabled={isSaving}>
								Cancel
							</Button>
						</div>
					</>
				) : (
					<>
						{hasAnyData ? (
							<BusinessInfoDisplay businessInfo={parsedBusinessInfo} keywords={keywords} />
						) : (
							<>
								<p className={css(styles.notConfiguredText)}>Not configured</p>
								<div className={css(styles.customContentWebsiteUrl)}>
									<div className={css(styles.infoFieldLabel)}>
										Enter a website URL to help us generate your preferences based on your site
									</div>
									<Input
										type='url'
										value={websiteUrl}
										onChange={e => setWebsiteUrl(e.target.value)}
										placeholder='https://example.com'
									/>
								</div>
							</>
						)}
						<div className={css(styles.buttonContainer)}>
							<Button variant='secondary' size='sm' onClick={() => setAiAssistantOpen(true)}>
								{buttonText}
							</Button>
							<Button variant='secondary' size='sm' onClick={handleEditDirectlyClick}>
								Edit Directly
							</Button>
						</div>
					</>
				)}
			</section>

			<AIAssistantDialog
				open={aiAssistantOpen}
				onOpenChange={handleOpenChange}
				assistantName='AccountOnboardAgent'
				conversationType={AIConversationType.AccountOnboarding}
				title='Custom Content Preferences'
				initialMessage={initialMessage}
				placeholder='Send a message...'
				impersonationContext={impersonationContext}
				customInitialAction={customInitialAction}
				disablePointerDismissal={true}
			/>
		</div>
	);
};

const CustomContentPreferencesModalAsObserver = observer(CustomContentPreferencesModalSFC);
const CustomContentPreferencesModalWithContext = inject(ModalChildComponentContextKey)(
	CustomContentPreferencesModalAsObserver
);
export const CustomContentPreferencesModal = asModalComponent(CustomContentPreferencesModalWithContext, {
	className: 'custom-content-preferences-modal',
	useDefaultHeader: true,
	shouldCloseOnOverlayClick: false,
});
